<template>
  <b-container
    class="parameters-tab"
    fluid
  >
    <div class="parameters-tab__filters">
      <b-row
        align-v="center"
        align-content="between"
        class="py-3"
      >
        <b-col
          xs="12"
          sm="8"
        >
          <label>
            {{ $t('engineering.physical-chemical.parameters-tab.select-norm') }}
          </label>
          <s-select
            v-model="selectedNorm"
            :options="norms"
            :disabled="disableSelectNorm"
            @input="normChanged"
          />
        </b-col>
        <b-col
          v-show="selectedNorm"
          xs="12"
          sm="4"
        >
          <b-checkbox
            class="parameters-tab__checkbox"
            v-model="isNormAlarmShowing"
            :disabled="disableShowNormAlarmCheckbox"
            @change="onChangeCheckbox"
          >
            {{ $t('engineering.physical-chemical.parameters-tab.show-norm-alarm') }}
          </b-checkbox>
        </b-col>
      </b-row>
      <hr
        v-show="selectedNorm"
        class="parameters-tab__filter-divider"
      >
      <b-row v-show="selectedNorm">
        <b-col
          xs="12"
          sm="8"
        >
          <label>
            {{ $t('engineering.oil-sample') }}
          </label>
          <s-select
            v-model="selectedType"
            :options="mappedSampleTypes"
            :disabled="disableOilSample"
            @input="typeChanged"
          />
        </b-col>
      </b-row>
    </div>
    <s-loader
      v-if="loading"
      class="p-5 parameters-tab__loader"
      size="lg"
    />
    <s-empty-list v-else-if="showTable && !hasParameters" />
    <b-row
      v-else-if="showTable"
      class="p-3"
    >
      <b-col
        cols=12
        class="px-0"
      >
        <parameters-table
          show-category
          :table="table"
          :column-inserting.sync="columnInserting"
          :column-editing.sync="columnEditing"
          :is-editing="isEditingTable"
          :parameters="parameters"
          @hide-lots="hideLots"
          @add-custom="addCustomValue"
          @edit-custom="editCustomValue"
          @delete-custom="confirmDelete"
        />
      </b-col>
    </b-row>
    <b-row
      v-show="isEditingTable"
      class="parameters-tab__footer-buttons-row"
    >
      <b-col>
        <b-button
          variant="secondary"
          @click="cancel()"
        >
          {{ $t('general.cancel') }}
        </b-button>
        <b-button
          variant="primary"
          @click="confirmSave()"
        >
          {{ $t('general.save') }}
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import pullAt from 'lodash/pullAt';
import ParametersService from '@/domain/services/installation/engineering/physicalChemical/parameters';
import eventBus from '@/services/globalEventBus';
import engineeringEvents from '@/domain/events/engineering';
import { sampleType as sampleTypes } from '@/domain/enums/sample';
import { Norms } from 'sigma-mappings';
import cloneDeep from 'lodash/cloneDeep';
import parameterTable from '@/domain/data/installation/engineering/physicalChemical/parameterTable';
import ParametersTable from './parameters/ParametersTable';

export default {
  name: 'ParametersTab',
  components: {
    ParametersTable,
  },
  data: () => ({
    loading: null,
    selectedNorm: null,
    selectedType: null,
    columnInserting: null,
    columnEditing: null,
    parametersCopy: [],
    isNormAlarmShowing: false,
    table: {},
    loadingAllParametersNormHidden: false,
    isAllParametersNormHidden: false,
  }),
  computed: {
    parameters: {
      get() {
        return (this.table.tableHorizontal || {}).parameters || [];
      },
      set(value) {
        (this.table.tableHorizontal || {}).parameters = value;
      },
    },
    disableSelectNorm() {
      return this.isEditingTable || this.loadingAllParametersNormHidden || this.loading;
    },
    isEditingTable() {
      return this.columnInserting !== null || this.columnEditing !== null;
    },
    assetModuleId() {
      return (this.$route.params || {}).assetModuleId;
    },
    hasParameters() {
      return this.parameters.length;
    },
    showTable() {
      return this.selectedNorm && this.selectedType;
    },
    disableOilSample() {
      return this.isEditingTable
        || !this.selectedNorm
        || this.loadingAllParametersNormHidden
        || this.loading;
    },
    norms() {
      return [
        {
          text: this.$t('general.select'),
          value: null,
        },
        {
          text: this.$t('engineering.physical-chemical.parameters-tab.iec-60422-2013'),
          value: Norms.IEC_60422_2013,
        },
        {
          text: this.$t('engineering.physical-chemical.parameters-tab.ieee-c57-106-2015'),
          value: Norms.IEEE_C57_106_2015,
        },
        {
          text: this.$t('engineering.physical-chemical.parameters-tab.nbr-10576-2012'),
          value: Norms.NBR_10576_2012,
        },
        {
          text: this.$t('engineering.physical-chemical.parameters-tab.nbr-10576-2017'),
          value: Norms.NBR_10576_2017,
        },
      ];
    },
    sampleTypes() {
      return [
        {
          text: this.$t('enums.sample-type.used'),
          value: sampleTypes.USED,
        },
        {
          text: this.$t('enums.sample-type.new'),
          value: sampleTypes.NEW,
        },
        {
          text: this.$t('enums.sample-type.recondicioned'),
          value: sampleTypes.RECONDICIONED,
        },
        {
          text: this.$t('enums.sample-type.regenerated'),
          value: sampleTypes.REGENERATED,
        },
      ];
    },
    normsWithTypes() {
      return {
        [Norms.NBR_10576_2012]: [
          sampleTypes.NEW,
          sampleTypes.RECONDICIONED,
          sampleTypes.REGENERATED,
          sampleTypes.USED,
        ],
        [Norms.NBR_10576_2017]: [
          sampleTypes.NEW,
          sampleTypes.RECONDICIONED,
          sampleTypes.REGENERATED,
          sampleTypes.USED,
        ],
        [Norms.IEC_60422_2013]: [
          sampleTypes.NEW,
          sampleTypes.USED,
        ],
        [Norms.IEEE_C57_106_2015]: [
          sampleTypes.NEW,
          sampleTypes.RECONDICIONED,
          sampleTypes.REGENERATED,
          sampleTypes.USED,
        ],
      };
    },
    mappedSampleTypes() {
      const nullOption = {
        text: this.$t('general.select'),
        value: null,
      };
      const typesFromNorm = this.normsWithTypes[this.selectedNorm];
      if (!typesFromNorm) return [nullOption];
      const mappedTypesFromNorm = typesFromNorm.map(
        typeFromNorm => this.sampleTypes.find(sampleType => sampleType.value === typeFromNorm),
      );
      return [
        nullOption,
        ...mappedTypesFromNorm,
      ];
    },
    disableShowNormAlarmCheckbox() {
      return !this.$hasUserPermission('PER013')
        || this.isAllParametersNormHidden
        || this.loading
        || this.loadingAllParametersNormHidden;
    },
  },
  created() {
    this.init();
  },
  mounted() {
    this.table = parameterTable;
  },
  beforeDestroy() {
    this.deinit();
  },
  methods: {
    init() {
      eventBus.on(engineeringEvents.refreshData, this.refreshDataEvent, this);
    },
    deinit() {
      eventBus.off(engineeringEvents.refreshData, this.refreshDataEvent, this);
    },
    refreshDataEvent({ wait }) {
      if (!this.selectedType || !this.selectedNorm) return;
      wait(this.getTableItems());
    },
    setParametersCopy() {
      this.parametersCopy = cloneDeep(this.parameters);
    },
    editCustomValue(column, columnIndex) {
      this.setParametersCopy();
      this.columnEditing = columnIndex;
    },
    addCustomValue(column, columnIndex) {
      const customColumn = cloneDeep(column);
      const copiedFromDefault = !column.custom;
      customColumn.name = '';
      customColumn.id = '';
      customColumn.custom = true;
      customColumn.enabled = true;
      customColumn.copiedFromDefault = copiedFromDefault;
      customColumn.originCustomizationName = copiedFromDefault ? null : column.name;
      this.parameters.splice(columnIndex, 0, customColumn);
      this.columnInserting = columnIndex;
    },
    deleteCustomValue(columnIndex) {
      const { id } = this.parameters[columnIndex] || {};
      this.loading = true;
      ParametersService.deleteParameter(this.assetModuleId, id)
        .then(() => {
          this.$pushAlert({ code: 'MS030' });
          this.getTableItems();
        }).finally(() => { this.loading = false; });
    },
    confirmDelete(columnIndex) {
      this.$confirmMessage({ code: 'MC043' }).accepted(() => {
        this.deleteCustomValue(columnIndex);
      });
    },
    exitInserting() {
      pullAt(this.parameters, this.columnInserting);
      this.columnInserting = null;
    },
    exitEditing() {
      this.table.tableHorizontal.parameters = this.parametersCopy;
      this.columnEditing = null;
    },
    confirmSave() {
      this.$confirmMessage({ code: 'MC038' })
        .accepted(() => {
          this.saveOrUpdate();
        });
    },
    saveOrUpdate() {
      if (this.columnEditing !== null) {
        this.update();
        return;
      }
      this.save();
    },
    mountPayload(isEditing) {
      const columnIndex = isEditing ? this.columnEditing : this.columnInserting;
      const parameter = this.parameters[columnIndex] || {};
      return {
        id: parameter.id,
        norm: this.selectedNorm,
        sampleType: this.selectedType,
        name: parameter.name,
        rules: parameter.rules,
        copiedFromDefault: parameter.copiedFromDefault,
        originCustomizationName: parameter.originCustomizationName,
      };
    },
    update() {
      const payload = this.mountPayload(true);
      ParametersService.putParameter(this.assetModuleId, payload).then(() => {
        this.columnEditing = null;
        this.setParametersCopy();
        this.$pushAlert({ code: 'MS016' });
      });
    },
    save() {
      const payload = this.mountPayload(false);
      ParametersService.postParametersTable(this.assetModuleId, payload).then(() => {
        this.columnInserting = null;
        this.$pushAlert({ code: 'MS016' });
        this.getTableItems(false);
      });
    },
    cancel() {
      this.$confirmMessage({ code: 'MC006' })
        .accepted(() => {
          this.exitInsertOrEdit();
        });
    },
    exitInsertOrEdit() {
      if (this.columnEditing !== null) {
        this.exitEditing();
        return;
      }
      this.exitInserting();
    },
    onResolveToggleVariableVisibility(enabled) {
      this.$pushAlert({ code: 'MS016' });
      if (enabled) {
        this.getNormAlarm();
      }
    },
    hideLots({ enabled, id }) {
      this.loading = true;
      ParametersService.toggleVariableVisibility(this.assetModuleId, id, enabled)
        .then(() => {
          this.onResolveToggleVariableVisibility(enabled);
        })
        .finally(() => {
          Promise.allSettled([
            this.getTableItems(),
            this.getAllParametersNormHiddenState(),
          ])
            .then(() => {
              this.loading = false;
            });
        });
    },
    handleResponseError(error) {
      const code = error.response.data && error.response.data.codigoMensagem;
      if (code === 'ME052') {
        this.parameters = [];
      }
    },
    mapParametersTableResponse(response) {
      this.table.tableHorizontal = {
        ...response,
        parameters: (response.parameters || []).map(
          item => ({ ...item, name: item.name || this.$t('enums.engineering.diagnostic-rule.standard') }),
        ),
      };
      this.sortParameters();
    },
    sortParameters() {
      const { parameters } = this.table.tableHorizontal;
      parameters
        .sort((first, second) => first.custom - second.custom || second.enabled - first.enabled);
    },
    getTableItems(doLoading = true) {
      const payload = {
        sampleType: this.selectedType,
        norm: this.selectedNorm,
      };
      this.loading = doLoading;
      return ParametersService.getParametersTable(this.assetModuleId, payload)
        .then((response) => {
          this.mapParametersTableResponse(response);
        }).catch((error) => {
          this.handleResponseError(error);
        }).finally(() => {
          this.loading = false;
        });
    },
    toggleNormAlarm() {
      ParametersService.toggleNormAlarm(
        this.assetModuleId,
        this.isNormAlarmShowing,
        this.selectedNorm,
      ).then(() => {
        this.isNormAlarmShowing = !this.isNormAlarmShowing;
        this.$pushAlert({ code: 'MS016' });
      });
    },
    onChangeCheckbox() {
      this.$nextTick(() => { this.isNormAlarmShowing = !this.isNormAlarmShowing; });
      const code = this.isNormAlarmShowing ? 'MC044' : 'MC045';
      this.$confirmMessage({
        code,
        messageParams: {
          norm: this.$t(`engineering.physical-chemical.parameters-tab.${this.selectedNorm}`),
        },
      }).accepted(() => {
        this.toggleNormAlarm();
      });
    },
    getNormAlarm() {
      if (!this.selectedNorm) return;
      ParametersService.getNormAlarm(this.assetModuleId, this.selectedNorm)
        .then((response) => {
          this.isNormAlarmShowing = response.enabled;
        })
        .catch(() => {
          this.isNormAlarmShowing = false;
        });
    },
    normChanged() {
      this.getNormAlarm();
      this.getAllParametersNormHiddenState();
      this.selectedType = null;
    },
    typeChanged() {
      if (this.selectedType) {
        this.parametersCopy = [];
        this.getTableItems();
      }
    },
    getAllParametersNormHiddenState() {
      this.loadingAllParametersNormHidden = true;

      const payload = {
        norm: this.selectedNorm,
      };

      return ParametersService.getAllParametersNormHiddenState(this.assetModuleId, payload)
        .then((response) => {
          this.isAllParametersNormHidden = response;
        })
        .finally(() => {
          this.loadingAllParametersNormHidden = false;
        });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.parameters-tab
  &__filters
    margin-bottom: 20px
  &__checkbox
    margin-top: 25px
  &__filter-divider
    border-top: 1px solid $color-gray-medium-highlight
  &__footer-buttons-row
    text-align: right
</style>
